import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Home, PlayCircle, MessageCircle, Search, User, Bell, X, Plus, Calendar, CreditCard, Users } from 'lucide-react';
import FeedTab from './tabs/FeedTab';
import ReelsTab from './tabs/ReelsTab';
import ChatTab from './tabs/ChatTab';
import SearchTab from './tabs/SearchTab';
import ProfileTab from './tabs/ProfileTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'feed', label: 'Home', icon: Home },
  { id: 'reels', label: 'Reels', icon: PlayCircle },
  { id: 'chat', label: 'Chat', icon: MessageCircle },
  { id: 'search', label: 'Search', icon: Search },
  { id: 'profile', label: 'Profile', icon: User },
];

export default function MainApp({ initialTab }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || initialTab || 'feed');
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentSession, setPaymentSession] = useState(null);
  const [toastMsg, setToastMsg] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [viewProfileId, setViewProfileId] = useState(null);
  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}` };

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    fetchUser();
    // Handle URL params (payment return, calendar link, etc.)
    const tab = searchParams.get('tab');
    const payment = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    const calendar = searchParams.get('calendar');
    if (tab) setActiveTab(tab);
    if (payment === 'success' && sessionId) {
      setPaymentSession(sessionId);
      // Check if this is a wallet top-up
      const isTopup = searchParams.get('topup');
      if (isTopup) {
        setActiveTab('profile');
        // Poll topup status
        const pollTopup = async () => {
          for (let i = 0; i < 8; i++) {
            try {
              const res = await fetch(`${API_URL}/api/wallet/topup/status/${sessionId}`, { headers: { 'Authorization': `Bearer ${token}` } });
              if (res.ok) {
                const d = await res.json();
                if (d.status === 'paid') { showToast(`Wallet topped up! Balance: ₹${d.balance}`); fetchUser(); return; }
              }
            } catch {}
            await new Promise(r => setTimeout(r, 2500));
          }
        };
        pollTopup();
      } else {
        setActiveTab('chat');
      }
      showToast('Payment successful! Processing...');
    }
    if (payment === 'cancelled') showToast('Payment cancelled');
    if (calendar === 'linked') { setActiveTab('profile'); showToast('Google Calendar linked!'); }
    // Clean URL params
    if (searchParams.toString()) setSearchParams({}, { replace: true });
  }, []);

  const fetchUser = async () => {
    try {
      const res = await fetch(`${API_URL}/api/auth/me`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) { setUser(await res.json()); }
      else { localStorage.removeItem('token'); navigate('/login'); }
    } catch { navigate('/login'); }
    setLoading(false);
  };

  const showToast = (msg) => { setToastMsg(msg); setTimeout(() => setToastMsg(''), 3000); };

  const fetchUnread = async () => {
    try {
      const res = await fetch(`${API_URL}/api/notifications/unread-count`, { headers });
      if (res.ok) { const d = await res.json(); setUnreadCount(d.count || 0); }
    } catch {}
  };

  const openNotifPanel = async () => {
    setShowNotifPanel(true);
    try {
      const res = await fetch(`${API_URL}/api/notifications`, { headers });
      if (res.ok) setNotifications(await res.json());
      // Mark as read
      await fetch(`${API_URL}/api/notifications/mark-read`, { method: 'POST', headers });
      setUnreadCount(0);
    } catch {}
  };

  // Register Web Push on mount
  useEffect(() => {
    if (!token || !('serviceWorker' in navigator) || !('PushManager' in window)) return;
    const registerPush = async () => {
      try {
        const registration = await navigator.serviceWorker.register('/sw-push.js');
        const keyRes = await fetch(`${API_URL}/api/push/vapid-key`);
        if (!keyRes.ok) return;
        const { public_key } = await keyRes.json();
        if (!public_key) return;
        // Convert VAPID key
        const padding = '='.repeat((4 - public_key.length % 4) % 4);
        const raw = atob((public_key + padding).replace(/-/g, '+').replace(/_/g, '/'));
        const arr = new Uint8Array(raw.length);
        for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
        const subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: arr });
        await fetch(`${API_URL}/api/push/subscribe`, {
          method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify(subscription.toJSON()),
        });
      } catch {}
    };
    registerPush();
  }, [token]);

  // Poll unread count every 30s
  useEffect(() => {
    if (!token) return;
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, [token]);

  if (loading || !user) return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const viewProfile = (userId) => {
    if (userId === user?.user_id) {
      setViewProfileId(null);
      setActiveTab('profile');
    } else {
      setViewProfileId(userId);
      setActiveTab('profile');
    }
  };

  const renderTab = () => {
    switch (activeTab) {
      case 'feed': return <FeedTab user={user} showToast={showToast} onProfileClick={viewProfile} />;
      case 'reels': return <ReelsTab user={user} onProfileClick={viewProfile} showToast={showToast} />;
      case 'chat': return <ChatTab user={user} paymentSession={paymentSession} clearPaymentSession={() => setPaymentSession(null)} showToast={showToast} onProfileClick={viewProfile} />;
      case 'search': return <SearchTab user={user} onStartChat={(u) => { setActiveTab('chat'); }} onProfileClick={viewProfile} />;
      case 'profile': return <ProfileTab user={user} isOwnProfile={!viewProfileId || viewProfileId === user?.user_id} viewUserId={viewProfileId} refreshUser={fetchUser} showToast={showToast} onBack={() => { setViewProfileId(null); setActiveTab('feed'); }} onProfileClick={viewProfile} onSwitchTab={setActiveTab} />;
      default: return <FeedTab user={user} showToast={showToast} onProfileClick={viewProfile} />;
    }
  };

  return (
    <div className="h-screen flex flex-col bg-black text-white" data-testid="main-app">
      {/* Toast */}
      {toastMsg && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 bg-zinc-800 border border-zinc-700 text-white text-sm px-4 py-2.5 rounded-xl shadow-xl animate-pulse" data-testid="toast">
          {toastMsg}
        </div>
      )}

      {/* Top Bar: New Content | Connect Logo | Notification Bell */}
      <div className="shrink-0 flex items-center justify-between px-4 py-2.5 border-b border-zinc-900" data-testid="top-bar">
        <button onClick={() => { setActiveTab('feed'); setTimeout(() => window.dispatchEvent(new CustomEvent('open-new-post')), 100); }}
          className="p-2.5 rounded-xl bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 transition-colors" data-testid="new-content-btn">
          <Plus className="w-6 h-6 text-white" />
        </button>
        <div className="flex items-center gap-1.5" data-testid="app-logo">
          <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-orange-500 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/20">
            <span className="text-white font-extrabold text-sm">C</span>
          </div>
          <span className="text-lg font-extrabold bg-gradient-to-r from-purple-400 to-orange-400 bg-clip-text text-transparent">Connect</span>
        </div>
        <button onClick={openNotifPanel} className="relative p-2.5 rounded-xl bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 transition-colors" data-testid="notif-bell">
          <Bell className="w-6 h-6 text-zinc-400 hover:text-white transition-colors" />
          {unreadCount > 0 && (
            <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1" data-testid="notif-badge">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
      </div>

      {/* Notification Panel — 3-Tab Popup */}
      {showNotifPanel && (
        <NotificationPanel
          onClose={() => setShowNotifPanel(false)}
          headers={headers}
          onNavigate={(tab) => { setShowNotifPanel(false); setActiveTab(tab); }}
          onProfileClick={(uid) => { setShowNotifPanel(false); viewProfile(uid); }}
        />
      )}
      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {renderTab()}
      </div>

      {/* Bottom Navigation */}
      <nav className="shrink-0 bg-black border-t border-zinc-800 px-2 pb-[env(safe-area-inset-bottom)]" data-testid="bottom-nav">
        <div className="flex items-center justify-around h-12">
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex flex-col items-center justify-center py-1 px-3 transition-colors ${activeTab === tab.id ? 'text-white' : 'text-zinc-600'}`}
              data-testid={`tab-${tab.id}`}>
              <tab.icon className={`w-6 h-6 ${activeTab === tab.id ? 'stroke-[2.5]' : ''}`} />
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
}


function NotificationPanel({ onClose, headers, onNavigate, onProfileClick }) {
  const [activeTab, setActiveTab] = useState('schedule');
  const [notifs, setNotifs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({ schedule: 0, payment: 0, social: 0 });

  useEffect(() => { fetchCounts(); fetchNotifs('schedule'); }, []);

  const fetchCounts = async () => {
    try {
      const res = await fetch(`${API_URL}/api/notifications/unread-count`, { headers });
      if (res.ok) { const d = await res.json(); setCounts({ schedule: d.schedule || 0, payment: d.payment || 0, social: d.social || 0 }); }
    } catch {}
  };

  const fetchNotifs = async (cat) => {
    setLoading(true);
    setActiveTab(cat);
    try {
      const res = await fetch(`${API_URL}/api/notifications?category=${cat}`, { headers });
      if (res.ok) setNotifs(await res.json());
    } catch {}
    setLoading(false);
  };

  const markAllRead = async () => {
    try {
      await fetch(`${API_URL}/api/notifications/mark-read?category=${activeTab}`, { method: 'POST', headers });
      setCounts(prev => ({ ...prev, [activeTab]: 0 }));
      setNotifs(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch {}
  };

  const timeAgo = (ts) => {
    if (!ts) return '';
    const diff = (Date.now() - new Date(ts).getTime()) / 1000;
    if (diff < 60) return 'now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
  };

  const TABS = [
    { id: 'schedule', label: 'Schedule', icon: Calendar },
    { id: 'payment', label: 'Payments', icon: CreditCard },
    { id: 'social', label: 'Social', icon: Users },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm" onClick={onClose} data-testid="notif-panel">
      <div className="absolute right-0 top-0 w-full max-w-sm h-full bg-zinc-950 border-l border-zinc-800 flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-zinc-800 shrink-0">
          <h3 className="font-bold text-base">Notifications</h3>
          <div className="flex items-center gap-2">
            <button onClick={markAllRead} className="text-[10px] text-purple-400 hover:text-purple-300 font-medium" data-testid="mark-all-read">Mark all read</button>
            <button onClick={onClose}><X className="w-5 h-5 text-zinc-400" /></button>
          </div>
        </div>
        {/* 3 Tab Buttons */}
        <div className="flex border-b border-zinc-800 shrink-0">
          {TABS.map(t => (
            <button key={t.id} onClick={() => fetchNotifs(t.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-xs font-semibold transition-all border-b-2 ${activeTab === t.id ? 'border-white text-white' : 'border-transparent text-zinc-500'}`}
              data-testid={`notif-tab-${t.id}`}>
              <t.icon className="w-3.5 h-3.5" />
              {t.label}
              {counts[t.id] > 0 && <span className="min-w-[16px] h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center px-1">{counts[t.id]}</span>}
            </button>
          ))}
        </div>
        {/* Notification list */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12"><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /></div>
          ) : notifs.length === 0 ? (
            <div className="p-8 text-center text-zinc-500 text-sm">No {activeTab} notifications</div>
          ) : (
            <div className="divide-y divide-zinc-800/50">
              {notifs.map((n, i) => (
                <div key={n.notification_id || i}
                  className={`px-4 py-3 hover:bg-zinc-900/50 transition-colors cursor-pointer ${!n.is_read ? 'bg-zinc-900/30 border-l-2 border-l-purple-500' : ''}`}
                  onClick={() => { if (n.data?.user_id) onProfileClick?.(n.data.user_id); else if (n.data?.booking_id) onNavigate?.('profile'); }}
                  data-testid={`notif-item-${i}`}>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center shrink-0 mt-0.5">
                      {activeTab === 'schedule' && <Calendar className="w-3.5 h-3.5 text-blue-400" />}
                      {activeTab === 'payment' && <CreditCard className="w-3.5 h-3.5 text-emerald-400" />}
                      {activeTab === 'social' && <Users className="w-3.5 h-3.5 text-purple-400" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white">{n.title || n.type}</p>
                      <p className="text-xs text-zinc-400 mt-0.5 line-clamp-2">{n.message}</p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <span className="text-[10px] text-zinc-600">{timeAgo(n.created_at)}</span>
                      {!n.is_read && <div className="w-2 h-2 rounded-full bg-purple-500" />}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
